import { useEffect, useRef, useState } from 'react';

import {
  Wrapper,
  DeckSvg10,
  DeckSvg11,
  DeckSvg12,
  BigBox,
  SmallBox,
  BoxesWrapper,
  TileCounter,
  CounterElement,
  CounterElementInner,
} from './styles';
import Dropzone from './Dropzone';
import SeeThroughBoxes from './SeeThroughBoxes';
import { Row, StatusDot } from '../styles';
import { inverseModuleStatuses } from '../enums';
import { ContentWrapper, SiScore, TsCode } from './Dropzone/styles';
import { getModuleRowBackgroundColor } from '../utils';
import { BOX_DOWN, BOX_MIDDLE, BOX_UP, DROPZONE_DOWN, DROPZONE_UP, DROPZONE_MANIFOLD_DOWN, DROPZONE_MANIFOLD_UP } from './consts';

const adaptModuleForDropzone = (module) => ({
  ...module,
  i: module.id,
  h: 1,
  isDraggable: true,
  static: false,
  w: module.length,
  x: Math.max(0, module.frameNr - 49),
  y: 0,
});

const Layout = ({ nrOfFrames, modules, onDropZoneItemClick, onDropZoneClick, onDropFromOutside, onDropFromInside }) => {
  const [layoutUp, setLayoutUp] = useState([]);
  const [layoutDown, setLayoutDown] = useState([]);
  const [layoutManifoldUp, setLayoutManifoldUp] = useState([]);
  const [layoutManifoldDown, setLayoutManifoldDown] = useState([]);
  const [boxUp, setBoxUp] = useState(null);
  const [boxMiddle, setBoxMiddle] = useState(null);
  const [boxDown, setBoxDown] = useState(null);
  const boxUpRef = useRef();
  const boxMiddleRef = useRef();
  const boxDownRef = useRef();

  const dropzoneProps = { onDropZoneItemClick, onDropZoneClick, onDropFromInside };

  const nrOfModules = nrOfFrames ? Math.round(nrOfFrames / 5) + 2 : 11;
  const tileCounterNumbers = [...new Array(nrOfModules)].map((_, i) => 45 + i * 5);

  useEffect(() => {
    const up = [];
    const manifoldUp = [];
    const down = [];
    const manifoldDown = [];
    let boxUp = null;
    let boxMiddle = null;
    let boxDown = null;

    modules.forEach((m) => {
      const module = adaptModuleForDropzone(m);
      if (module.frameNr >= 49) {
        const isManifoldModule = module.family.startsWith('TS041');
        if (m.position === 'Portside') {
          if (isManifoldModule) {
            manifoldUp.push(module);
          } else {
            up.push(module);
          }
        } else if (m.position === 'Starboard') {
          if (isManifoldModule) {
            manifoldDown.push(module);
          } else {
            down.push(module);
          }
        }
      } else if (module.frameNr === 44) {
        if (m.position === 'Portside') {
          boxUp = module;
        } else if (m.position === 'Starboard') {
          boxDown = module;
        } else {
          boxMiddle = module;
        }
      }
    });
    setLayoutUp(up);
    setLayoutManifoldUp(manifoldUp);
    setLayoutDown(down);
    setLayoutManifoldDown(manifoldDown);
    setBoxUp(boxUp);
    setBoxMiddle(boxMiddle);
    setBoxDown(boxDown);
  }, [modules]);

  const boxOnDrop = (event, boxId) => {
    const box = getBoxById(boxId);
    box.style.background = 'white';
    const projectData = JSON.parse(event.dataTransfer.getData('text'));

    onDropFromOutside(undefined, projectData, boxId);
  };
  const boxOnDragOver = (event, boxId) => {
    const box = getBoxById(boxId);
    box.style.background = 'lightgreen';
    event.preventDefault();
  };

  const boxOnDragLeave = (boxId) => {
    const box = getBoxById(boxId);
    box.style.backgroundColor = 'white';
  };

  const getBoxById = (boxId) =>
    boxId === BOX_UP ? boxUpRef.current : boxId === BOX_DOWN ? boxDownRef.current : boxMiddleRef.current;

  const deck = nrOfFrames === 50 ? <DeckSvg11/> : nrOfFrames === 55 ? <DeckSvg12/> : <DeckSvg10/>;

  return (
    <Wrapper>
      {deck}
      <SeeThroughBoxes
        position={{
          top: -10,
          left: 200,
        }}
      />
      <Dropzone
          nrOfFrames={nrOfFrames}
          layout={layoutManifoldUp}
          setLayout={setLayoutManifoldUp}
          position={{
            top: -9,
            left: 200,
          }}
          isManifold={true}
          {...dropzoneProps}
          onDropFromOutside={(layoutItemObj, project) => onDropFromOutside(layoutItemObj, project, DROPZONE_MANIFOLD_UP)}
      />
      <Dropzone
          nrOfFrames={nrOfFrames}
          layout={layoutUp}
          setLayout={setLayoutUp}
          position={{
            top: 40,
            left: 200,
          }}
          {...dropzoneProps}
          onDropFromOutside={(layoutItemObj, project) => onDropFromOutside(layoutItemObj, project, DROPZONE_UP)}
      />

      <Dropzone
          nrOfFrames={nrOfFrames}
          layout={layoutDown}
          setLayout={setLayoutDown}
          position={{
            top: 130,
            left: 200,
          }}
          {...dropzoneProps}
          isAbovePipes={false}
          onDropFromOutside={(layoutItemObj, project) => onDropFromOutside(layoutItemObj, project, DROPZONE_DOWN)}
      />
      <Dropzone
          nrOfFrames={nrOfFrames}
          layout={layoutManifoldDown}
          setLayout={setLayoutManifoldDown}
          position={{
            top: 197,
            left: 200,
          }}
          {...dropzoneProps}
          isManifold={true}
          isAbovePipes={false}
          onDropFromOutside={(layoutItemObj, project) => onDropFromOutside(layoutItemObj, project, DROPZONE_MANIFOLD_DOWN)}
      />
      <BoxesWrapper>
        <SmallBox
          ref={boxUpRef}
          onClick={() => onDropFromOutside(undefined, undefined, BOX_UP)}
          onDrop={(event) => boxOnDrop(event, BOX_UP)}
          onDragLeave={() => boxOnDragLeave(BOX_UP)}
          onDragOver={(event) => boxOnDragOver(event, BOX_UP)}>
          {boxUp && (
            <ContentWrapper
              onClick={(e) => {
                e.stopPropagation();
                onDropZoneItemClick(boxUp.id);
              }}
              padding='0.15rem 0.25rem'
              bg={getModuleRowBackgroundColor(boxUp)}>
              <Row justify='space-between'>
                <StatusDot
                  size='0.6rem'
                  color={inverseModuleStatuses[boxUp.status]?.color}
                />
                <SiScore fontSize='0.65rem'>{boxUp.siScore ?? '-'}%</SiScore>
              </Row>
              <Row justify='end'>
                <TsCode fontSize='0.6rem'>{boxUp.tsCode}</TsCode>
              </Row>
            </ContentWrapper>
          )}
        </SmallBox>
        <BigBox
          ref={boxMiddleRef}
          onClick={() => onDropFromOutside(undefined, undefined, BOX_MIDDLE)}
          onDrop={(event) => boxOnDrop(event, BOX_MIDDLE)}
          onDragLeave={() => boxOnDragLeave(BOX_MIDDLE)}
          onDragOver={(event) => boxOnDragOver(event, BOX_MIDDLE)}>
          {boxMiddle && (
            <ContentWrapper
              onClick={(e) => {
                e.stopPropagation();
                onDropZoneItemClick(boxMiddle.id);
              }}
              bg={getModuleRowBackgroundColor(boxMiddle)}
              padding='0.5rem 0.375rem'>
              <Row justify='space-between'>
                <StatusDot
                  size='0.8rem'
                  color={inverseModuleStatuses[boxMiddle.status]?.color}
                />
                <SiScore>{boxMiddle.siScore ?? '-'}%</SiScore>
              </Row>
              <Row justify='end'>
                <TsCode>{boxMiddle.tsCode}</TsCode>
              </Row>
            </ContentWrapper>
          )}
        </BigBox>
        <SmallBox
          ref={boxDownRef}
          onClick={() => onDropFromOutside(undefined, undefined, BOX_DOWN)}
          onDrop={(event) => boxOnDrop(event, BOX_DOWN)}
          onDragLeave={() => boxOnDragLeave(BOX_DOWN)}
          onDragOver={(event) => boxOnDragOver(event, BOX_DOWN)}>
          {boxDown && (
            <ContentWrapper
              onClick={(e) => {
                e.stopPropagation();
                onDropZoneItemClick(boxDown.id);
              }}
              padding='0.15rem 0.25rem'
              bg={getModuleRowBackgroundColor(boxDown)}>
              <Row justify='space-between'>
                <StatusDot
                  size='0.6rem'
                  color={inverseModuleStatuses[boxDown.status]?.color}
                />
                <SiScore fontSize='0.65rem'>{boxDown.siScore ?? '-'}%</SiScore>
              </Row>
              <Row justify='end'>
                <TsCode fontSize='0.6rem'>{boxDown.tsCode}</TsCode>
              </Row>
            </ContentWrapper>
          )}
        </SmallBox>
      </BoxesWrapper>
      <TileCounter nrOfModules={nrOfModules}>
        {tileCounterNumbers.map((num) => (
          <CounterElement>
            <CounterElementInner>{num}</CounterElementInner>
          </CounterElement>
        ))}
      </TileCounter>
    </Wrapper>
  );
};

export default Layout;
